import {forwardRef} from 'react';

import {Text} from '#component/base';

import {Icon} from '../Icon';

import {
  checkboxItemStyles,
  checkboxStyles,
  iconStyles,
  indicatorStyles,
  inputStyles,
  labelStyles,
  rootStyles,
} from './Checkbox.css';
/**
 * Checkbox inputs allow the user to select multiple options from a set.
 *
 * The Checkbox component renders a styled `<span>` element with a Checkbox `<input>`
 * as one of its children, for styling purposes.
 *
 * All HTML attributes are supported, and forwarded to the corresponding `input`
 * element. The className prop, however, is placed on the root node, to aid in
 * applying custom styles to any of the components children, via child
 * selectors.
 *
 * `ref`s are forwarded to the `input` element.
 */
export const Checkbox = forwardRef<
  HTMLInputElement,
  React.HTMLProps<HTMLInputElement> & {label?: React.ReactNode}
>(({className, checked, disabled, label = null, value, ...otherProps}, ref) => {
  return (
    <span className={`${className} ${checkboxItemStyles}`}>
      <span className={checkboxStyles}>
        <span className={rootStyles({disabled, checked})}>
          <input
            className={inputStyles}
            ref={ref}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            value={value}
            {...otherProps}
          />
          <span className={indicatorStyles({disabled})} />
          <Icon
            aria-hidden
            className={iconStyles}
            name="Check"
            size="xSmall"
            color={disabled ? 'dustyGray' : 'white'}
            weight="bold"
          />
        </span>
        {label !== null && (
          <Text variant="buttonSmall" className={labelStyles}>
            {label}
          </Text>
        )}
      </span>
    </span>
  );
});
